import { ReactElement, useMemo } from "react";
import {
	AppIcon,
	Highlight,
	HighlightedText,
	Icon,
	TagList,
	TagModel
} from "client/Components";
import {
	AppGlobalSearchResultItem,
	GlobalSearchScopeIdentifier,
	HighlightItem
} from "client/Api/TitleBar/GlobalSearch/globalSearch";
import { useHighlights } from "../../Layout/TitleBar/GlobalSearch/ResultView/CategoryView/ItemView/useHighlights";
import { ItemLayout } from "./ItemLayout";
import { categoryToNameMap } from "client/Features/Apps/Store/CategoryFilter";
import { localize } from "client_legacy/ndaw/culture";
import styles from "./AppItemView.module.less";
import { getReactColorAttributes } from "client_legacy/ndaw/appColor";
import { AppColorViewModel } from "client/Api/Apps/common";
import classNames from "classnames";
import { AppImageDisplayInfo } from "client/Api/AppIcon/common";
import { generateTrackingId, isNotNil } from "client/Misc/utils";
import { TimeZone } from "client/DateAndTime/timezones";
import { useAppNavigationHandler } from "client/Features/Layout/TitleBar/GlobalSearch/Navigation/useAppNavigationHandler";
import { SearchListItem } from "../SearchListItem";
import { Key } from "client/Misc/commonTypes";
import { useKeyboardListNavigationItem } from "client/Hooks";

export type AppItemViewProps = {
	item: AppGlobalSearchResultItem;
	navigationIndex: number;
	itemScope: GlobalSearchScopeIdentifier;
	timezone: TimeZone;
};

export const AppItemView = (props: AppItemViewProps): ReactElement => {
	const {
		item,
		navigationIndex,
		itemScope,
		timezone
	} = props;

	const {
		id,
		maintenanceWindow
	} = item;

	const executeHandler: () => void = useAppNavigationHandler({
		id,
		itemScope,
		maintenanceWindow,
		parameters: item,
		searchResult: item,
		timezone
	});

	return (
		<SearchListItem
			onExecute={executeHandler}
			trackingId={generateTrackingId("app-item", `|${item.id}`)}
		>
			<AppItem
				item={item}
				navigationIndex={navigationIndex}
				onExecute={executeHandler}
			/>
		</SearchListItem>
	);
};
AppItemView.displayName = "AppItemView";

type AppItemProps = {
	item: AppGlobalSearchResultItem;
	navigationIndex: number;
	onExecute: () => void;
};

const AppItem = (
	props: AppItemProps
): ReactElement => {
	const {
		item,
		navigationIndex,
		onExecute
	} = props;

	const {
		hitHighlights,
		name,
		category,
		color,
		image,
		maintenanceWindow,
		tags,
		tagsHitHighlights
	} = item;

	const highlights: Map<string, Highlight> = useHighlights(hitHighlights);

	const isInMaintenance: boolean = isNotNil(maintenanceWindow);

	const tagModels = useMemo(
		(): readonly TagModel<number>[] => {
			let checkedTagsHitHighlights: readonly (readonly HighlightItem[])[] | undefined = tagsHitHighlights;

			if (tags.length !== checkedTagsHitHighlights.length) {
				console.error("Collection of tags and collection with highlights for each tag do not match in length. Highlights will be ignored.");
				checkedTagsHitHighlights = undefined;
			}

			return tags.map(
				(tag, index): TagModel<number> => ({
					text: tag.name,
					value: tag.id,
					highlight: isNotNil(checkedTagsHitHighlights)
						? getTagNameHighlight(checkedTagsHitHighlights[index])
						: undefined,
					color: "secondary"
				})
			);
		},
		[tags, tagsHitHighlights]
	);

	const { ref, onKeyDown } = useKeyboardListNavigationItem({
		itemIndex: navigationIndex,
		onExecute,
		executeKey: [Key.Enter, Key.Space],
	});

	return (
		<ItemLayout
			trackingId="app-item-info"
			ref={ref}
			onKeyDown={onKeyDown}
			tabIndex={-1}
		>
			<ItemLayout.IconContainer>
				<AppItemIcon
					color={color}
					image={image}
					isInMaintenance={isInMaintenance}
				/>
			</ItemLayout.IconContainer>
			<ItemLayout.InfoWrapper>
				<ItemLayout.Title
					trackingId="name"
					title={name}
				>
					<HighlightedText
						text={name}
						highlight={highlights.get("name")}
					/>
					{isInMaintenance && (
						<span className={styles.maintenanceIconWrapper}>
							<Icon
								value={{
									name: "screwdriver-wrench",
									color: "danger",
									kind: "font"
								}}
							/>
						</span>
					)}
				</ItemLayout.Title>
				<ItemLayout.Info>
					<div className={styles.infoRow}>
						<ItemLayout.SubTitle trackingId="category">
							{localize(categoryToNameMap[category])}
						</ItemLayout.SubTitle>
						{tags.length > 0 && (
							<ItemLayout.SubTitle>|</ItemLayout.SubTitle>
						)}
						<div
							className={styles.tagsContainer}
							onClick={(e) => e.stopPropagation()}
						>
							<TagList
								tags={tagModels}
								showMoreTagsColor="secondary"
								size="small"
							/>
						</div>
					</div>
				</ItemLayout.Info>
			</ItemLayout.InfoWrapper>
		</ItemLayout>
	);
};

AppItem.displayName = "AppItem";

export type AppItemIconProps = {
	color: AppColorViewModel;
	image: AppImageDisplayInfo;
	isInMaintenance?: boolean;
};

export const AppItemIcon = (props: AppItemIconProps): ReactElement => {
	const {
		image,
		color,
		isInMaintenance = false
	} = props;

	const { colorClass, colorStyle } = getReactColorAttributes(color);

	return (
		<div
			className={
				classNames(
					styles.appItemThumbnail,
					colorClass,
					{
						[styles.appItemThumbnail_isInMaintenance]: isInMaintenance
					}
				)
			}
			style={colorStyle}
		>
			<div className={styles.appItemIcon}>
				<AppIcon value={image} color="inherit" />
			</div>
		</div>
	);
};

AppItemIcon.displayName = "AppItemIcon";


function getTagNameHighlight(tagHighlights: readonly HighlightItem[]): Highlight | undefined {
	const hitHighlight: HighlightItem | undefined = tagHighlights.find(h => h.highlightedField === "name");

	if (!hitHighlight) {
		return undefined;
	}

	return [
		hitHighlight.startPosition,
		hitHighlight.fragmentLength
	];
}